// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import {
  Checkbox,
  Text,
  Select,
  Option,
} from 'informed';
import ReactToolTip from 'react-tooltip';

import { Button } from '../Buttons.jsx';
import ContentBox from '../ContentBox.jsx';
import { Label } from '../Typography.jsx';
import NumericText from '../NumericText.jsx';
import DateText from '../DateText.jsx';
import Typeahead from '../Typeahead.jsx';

import {
  fetchSearchResults,
  fetchSearchResultsAdvanced,
  fetchSearchResultsForUpdates,
  fetchSearchResultsForUpdatesAdvanced,
} from '../../actions/searchActions';

import styles from '../../styles/SearchPage.scss';

const MIN_DATE = '01-01-1900';

// Help information displayed to the user.
export const generalInfoHelp = 'For all the filtering options below, you can '
  + 'assume the following unless specified otherwise. Left empty, date ranges '
  + 'default to 01-01-1900 through today. In general, text fields match as '
  + 'long as the patient row contains the query text (case-insensitive). For '
  + 'example, searching for "berry" would match with "Strawberry" or '
  + '"blueberry". Also as a result, if these fields are left blank, they have '
  + 'no effect on the search.';
export const houseHistoryHelp = 'Patients must match with at least one of the '
  + 'following rows. A patient matches with a row if he has lived in the '
  + 'specified house at some point within the specified date range. The house '
  + 'match must be exact (although case-insensitive).';
export const hospitalizationHistoryHelp = 'Patients must match with at least '
  + 'one of the following rows. A patient matches a row if he has ever been '
  + 'hospitalized with the specified parameters.';
export const dietHistoryHelp = 'Patients must match with at least one of the '
  + 'following rows. A patient matches a row if he has had a diet history with '
  + 'the specified parameters.';
export const testingHelp = 'Patients are compared to EVERY test listed below. '
  + 'If you wish to "ignore" a test, leave all its corresponding checkboxes '
  + 'checked. For each test, only the most recent test result is checked '
  + 'against. If a patient has no entry for a corresponding test, his value'
  + 'for that test is Unknown. For custom tests, the patient MUST have an '
  + 'entry corresponding to the test. An entry counts as a match if its name '
  + 'contains the query text (case-insensitive). For example, a query text of '
  + '"sickle" would match with a patient entry with the name "Sickle Cell".';
export const problemsHelp = 'Patients must match with at least one of the '
  + 'following rows. A patient matches a row if he has had a problem with the '
  + 'specified parameters.';
export const medicationsHelp = 'Patients must match with at least one of the '
  + 'following rows. A patient matches a row if he has had a medication with '
  + 'the specified parameters. If By Start Date is checked, then a medication '
  + 'matches the constraint only if it was started within the given range.';
export const allergiesHelp = 'Patients must match with at least one of the '
  + 'following rows. A patient matches a row if he has had an intervention '
  + 'with the specified parameters.';
export const interventionsHelp = 'Patients must match with at least one of the '
  + 'following rows. A patient matches a row if he has the allergy with the '
  + 'specified parameters.';
export const externalReportsHelp = 'Patients must match with at least one of '
  + 'the following rows. A patient matches a row if he has an external report '
  + 'with the specified parameters. '
  + "Note that Location field is not applicable for the Doctor's Notes and "
  + 'Referrals report type.';

type Props = {
  fetchSearchResults: Function,
  fetchSearchResultsAdvanced: Function,
  formApi: Object,
  location: Object,
  history: Object,
}

// The counters are used to assign unique ids.
type State = {
  advanced: boolean,
  generalInfoHelp: boolean,
  houseHistoryHelp: boolean,
  houseHistoryCounter: number,
  houseHistoryIds: number[],
  hospitalizationHistoryHelp: boolean,
  hospitalizationHistoryCounter: number,
  hospitalizationHistoryIds: number[],
  dietHistoryHelp: boolean,
  dietHistoryCounter: number,
  dietHistoryIds: number[],
  testingHelp: boolean,
  testingCounter: number,
  testingIds: number[],
  problemsHelp: boolean,
  problemsCounter: number,
  problemsIds: number[],
  medicationsHelp: boolean,
  medicationsCounter: number,
  medicationsIds: number[],
  interventionsHelp: boolean,
  interventionsCounter: number,
  interventionsIds: number[],
  allergiesHelp: boolean,
  allergiesCounter: number,
  allergiesIds: number[],
  externalReportsHelp: boolean,
  externalReportsCounter: number,
  externalReportsIds: number[],
  informedValues: Object,
}

// This component represents the search menu (including advanced options). It is
// used by both the Search Page (/search) and Updates Page (/updates).
class SearchMenuComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const paramsEncoded = new URLSearchParams(this.props.location.search).get('params');
    const params = paramsEncoded ? JSON.parse(atob(paramsEncoded)) : {};
    this.state = {
      advanced: !!Object.keys(params).length,
      generalInfoHelp: false,
      houseHistoryHelp: false,
      houseHistoryCounter: params.houseHistoryCounter || 0,
      houseHistoryIds: params.houseHistoryIds || [],
      hospitalizationHistoryHelp: false,
      hospitalizationHistoryCounter: params.hosptializationHistoryCounter || 0,
      hospitalizationHistoryIds: params.hospitalizationHistoryIds || [],
      dietHistoryHelp: false,
      dietHistoryCounter: params.dietHistoryCounter || 0,
      dietHistoryIds: params.dietHistoryIds || [],
      testingHelp: false,
      testingCounter: params.testingCounter || 0,
      testingIds: params.testingIds || [],
      problemsHelp: false,
      problemsCounter: params.problemsCounter || 0,
      problemsIds: params.problemsIds || [],
      medicationsHelp: false,
      medicationsCounter: params.medicationsCounter || 0,
      medicationsIds: params.medicationsIds || [],
      interventionsHelp: false,
      interventionsCounter: params.interventionsCounter || 0,
      interventionsIds: params.interventionIds || [],
      allergiesHelp: false,
      allergiesCounter: params.allergiesCounter || 0,
      allergiesIds: params.allergiesIds || [],
      externalReportsHelp: false,
      externalReportsCounter: params.externalReportsCounter || 0,
      externalReportsIds: params.externalReportsIds || [],
      informedValues: params,
    };
  }

  // On first mount, check if query parameters were used.
  componentDidMount() {
    if (this.props.location.search) {
      this.props.formApi.setValues(this.state.informedValues);
    }
  }

  // In order to restore search settings from either query parameters or
  // state (after closing the advanced search menu), formApi.setValues is called
  // after the component has been rendered.
  // Note, however, that this does not work with the Typeahead component, so for these
  // the initial value is set manually.
  componentDidUpdate(prevProps, prevState) {
    // Restore values when re-opening advanced search
    if (!prevState.advanced && this.state.advanced
        && Object.keys(this.state.informedValues).length) {
      this.props.formApi.setValues(this.state.informedValues);
    }
  }

  onSearch = () => {
    const { invalid, values } = this.props.formApi.getState();
    if (invalid) {
      return;
    }

    if (this.state.advanced) {
      const {
        houseHistoryIds,
        hospitalizationHistoryIds,
        dietHistoryIds,
        testingIds,
        problemsIds,
        medicationsIds,
        interventionsIds,
        allergiesIds,
        externalReportsIds,
        houseHistoryCounter,
        hospitalizationHistoryCounter,
        dietHistoryCounter,
        testingCounter,
        problemsCounter,
        medicationsCounter,
        interventionsCounter,
        allergiesCounter,
        externalReportsCounter,
      } = this.state;
      const params = {
        ...values,
        houseHistoryIds,
        hospitalizationHistoryIds,
        dietHistoryIds,
        testingIds,
        problemsIds,
        medicationsIds,
        interventionsIds,
        allergiesIds,
        externalReportsIds,
        houseHistoryCounter,
        hospitalizationHistoryCounter,
        dietHistoryCounter,
        testingCounter,
        problemsCounter,
        medicationsCounter,
        interventionsCounter,
        allergiesCounter,
        externalReportsCounter,
      };
      const paramsAsString = btoa(JSON.stringify(params));
      this.props.history.push(`${this.props.history.location.pathname}?params=${paramsAsString}`);
      this.props.fetchSearchResultsAdvanced(params);
    } else {
      this.props.fetchSearchResults(values);
    }
  }

  onKeyPress = (event) => {
    if (event.key === 'Enter') {
      this.onSearch();
    }
  }

  onToggleAdvancedOptions = () => {
    this.setState(
      (prevState) => {
        if (prevState.advanced) {
          return {
            ...prevState,
            advanced: false,
            informedValues: this.props.formApi.getState().values,
          };
        }
        return {
          ...prevState,
          advanced: true,
        };
      },
    );
  }

  toggleGeneralInfoHelp = () => {
    this.setState(prevState => ({
      ...prevState,
      generalInfoHelp: !prevState.generalInfoHelp,
    }));
  }

  houseHistoryRow = id => (
    <tr key={id}>
      <td>
        <Typeahead field={`house${id}`} size={16} endpoint='house' />
      </td>
      <td>
        <DateText field={`houseDateLower${id}`} item={{}} initEmpty />
      </td>
      <td>
        <DateText field={`houseDateUpper${id}`} item={{}} initEmpty />
      </td>
      <td>
        <Button onClick={() => this.deleteHouseHistoryRow(id)}>X</Button>
      </td>
    </tr>
  );

  toggleHouseHistoryHelp = () => {
    this.setState(prevState => ({
      ...prevState,
      houseHistoryHelp: !prevState.houseHistoryHelp,
    }));
  }

  addHouseHistoryRow = () => {
    this.setState(prevState => ({
      ...prevState,
      houseHistoryCounter: prevState.houseHistoryCounter + 1,
      houseHistoryIds: [...prevState.houseHistoryIds, prevState.houseHistoryCounter],
    }));
  }

  deleteHouseHistoryRow = (id) => {
    this.setState((prevState) => {
      const newIds = prevState.houseHistoryIds.filter(i => i !== id);
      return {
        ...prevState,
        houseHistoryIds: newIds,
      };
    });
  }

  hospitalizationHistoryRow = id => (
    <tr key={id}>
      <td>
        <Typeahead field={`hospitalLocation${id}`} size={12} endpoint='hospHxLocation' />
      </td>
      <td>
        <DateText field={`hospitalDateLower${id}`} item={{}} size={12} initEmpty />
      </td>
      <td>
        <DateText field={`hospitalDateUpper${id}`} item={{}} size={12} initEmpty />
      </td>
      <td>
        <Typeahead field={`hospitalDiagnosis${id}`} item={{}} size={12} endpoint='diagnosis' />
      </td>
      <td>
        <Text field={`hospitalTreatment${id}`} size={12} />
      </td>
      <td>
        <Text field={`dischargeInstructions${id}`} size={12} />
      </td>
      <td>
        <Button onClick={() => this.deleteHospitalizationHistoryRow(id)}>X</Button>
      </td>
    </tr>
  );

  toggleHospitalizationHistoryHelp = () => {
    this.setState(prevState => ({
      ...prevState,
      hospitalizationHistoryHelp: !prevState.hospitalizationHistoryHelp,
    }));
  }

  addHospitalizationHistoryRow = () => {
    this.setState(prevState => ({
      ...prevState,
      hospitalizationHistoryCounter: prevState.hospitalizationHistoryCounter + 1,
      hospitalizationHistoryIds: [...prevState.hospitalizationHistoryIds, prevState.hospitalizationHistoryCounter],
    }));
  }

  deleteHospitalizationHistoryRow = (id) => {
    this.setState((prevState) => {
      const newIds = prevState.hospitalizationHistoryIds.filter(i => i !== id);
      return {
        ...prevState,
        hospitalizationHistoryIds: newIds,
      };
    });
  }

  dietHistoryRow = id => (
    <tr key={id}>
      <td>
        <Checkbox field={`extraSnackYes${id}`} /> Yes   <Checkbox field={`extraSnackNo${id}`} /> No
      </td>
      <td>
        <Checkbox field={`highCalDietYes${id}`} /> Yes   <Checkbox field={`highCalDietNo${id}`} /> No
      </td>
      <td>
        <DateText field={`dietDateLower${id}`} item={{}} size={12} initEmpty />
      </td>
      <td>
        <DateText field={`dietDateUpper${id}`} item={{}} size={12} initEmpty />
      </td>
      <td>
        <Button onClick={() => this.deleteDietHistoryRow(id)}>X</Button>
      </td>
    </tr>
  );

  toggleDietHistoryHelp = () => {
    this.setState(prevState => ({
      ...prevState,
      dietHistoryHelp: !prevState.dietHistoryHelp,
    }));
  }

  addDietHistoryRow = () => {
    this.setState(prevState => ({
      ...prevState,
      dietHistoryCounter: prevState.dietHistoryCounter + 1,
      dietHistoryIds: [...prevState.dietHistoryIds, prevState.dietHistoryCounter],
    }));
  }

  deleteDietHistoryRow = (id) => {
    this.setState((prevState) => {
      const newIds = prevState.dietHistoryIds.filter(i => i !== id);
      return {
        ...prevState,
        dietHistoryIds: newIds,
      };
    });
  }

  testingRow = id => (
    <tr key={id}>
      <td>
        <Typeahead field={`test${id}`} size={16} endpoint='testName' />
      </td>
      <td>
        <Checkbox field={`test${id}Positive`} initialValue={true} /> Positive{'  '}
        <Checkbox field={`test${id}Negative`} initialValue={true} /> Negative{'  '}
        <Checkbox field={`test${id}Unknown`} initialValue={true} /> Unknown
      </td>
      <td>
        <Button onClick={() => this.deleteTestingRow(id)}>X</Button>
      </td>
    </tr>
  );

  toggleTestingHelp = () => {
    this.setState(prevState => ({
      ...prevState,
      testingHelp: !prevState.testingHelp,
    }));
  }

  addTestingRow = () => {
    this.setState(prevState => ({
      ...prevState,
      testingCounter: prevState.testingCounter + 1,
      testingIds: [...prevState.testingIds, prevState.testingCounter],
    }));
  }

  deleteTestingRow = (id) => {
    this.setState((prevState) => {
      const newIds = prevState.testingIds.filter(i => i !== id);
      return {
        ...prevState,
        testingIds: newIds,
      };
    });
  }

  problemsRow = id => (
    <tr key={id}>
      <td>
        <Checkbox field={`problem${id}Chronic`} initialValue={true} /> Chronic{'  '}
        <Checkbox field={`problem${id}Acute`} initialValue={true} /> Acute
      </td>
      <td>
        <Typeahead field={`problem${id}`} size={16} endpoint='diagnosis' />
      </td>
      <td>
        <DateText field={`problemDateLower${id}`} item={{}} size={12} initEmpty />
      </td>
      <td>
        <DateText field={`problemDateUpper${id}`} item={{}} size={12} initEmpty />
      </td>
      <td>
        <Button onClick={() => this.deleteProblemsRow(id)}>X</Button>
      </td>
    </tr>
  );

  toggleProblemsHelp = () => {
    this.setState(prevState => ({
      ...prevState,
      problemsHelp: !prevState.problemsHelp,
    }));
  }

  addProblemsRow = () => {
    this.setState(prevState => ({
      ...prevState,
      problemsCounter: prevState.problemsCounter + 1,
      problemsIds: [...prevState.problemsIds, prevState.problemsCounter],
    }));
  }

  deleteProblemsRow = (id) => {
    this.setState((prevState) => {
      const newIds = prevState.problemsIds.filter(i => i !== id);
      return {
        ...prevState,
        problemsIds: newIds,
      };
    });
  }

  medicationsRow = id => (
    <tr key={id}>
      <td>
        <Checkbox field={`medication${id}Chronic`} initialValue={true} /> Chronic{'  '}
        <Checkbox field={`medication${id}Acute`} initialValue={true} /> Acute
      </td>
      <td>
        <Typeahead field={`medication${id}`} size={16} endpoint='rxName' />
      </td>
      <td>
        <Checkbox field={`medicationByStartDate${id}`} initialValue={false} />
      </td>
      <td>
        <DateText field={`medicationDateLower${id}`} item={{}} size={12} initEmpty />
      </td>
      <td>
        <DateText field={`medicationDateUpper${id}`} item={{}} size={12} initEmpty />
      </td>
      <td>
        <Typeahead field={`medicationTime${id}`} size={16} endpoint='time' />
      </td>
      <td>
        <Typeahead field={`medicationRoute${id}`} size={16} endpoint='route' />
      </td>
      <td>
        <Button onClick={() => this.deleteMedicationsRow(id)}>X</Button>
      </td>
    </tr>
  );

  toggleMedicationsHelp = () => {
    this.setState(prevState => ({
      ...prevState,
      medicationsHelp: !prevState.medicationsHelp,
    }));
  }

  addMedicationsRow = () => {
    this.setState(prevState => ({
      ...prevState,
      medicationsCounter: prevState.medicationsCounter + 1,
      medicationsIds: [...prevState.medicationsIds, prevState.medicationsCounter],
    }));
  }

  deleteMedicationsRow = (id) => {
    this.setState((prevState) => {
      const newIds = prevState.medicationsIds.filter(i => i !== id);
      return {
        ...prevState,
        medicationsIds: newIds,
      };
    });
  }

  interventionsRow = id => (
    <tr key={id}>
      <td>
        <Checkbox field={`intervention${id}Chronic`} initialValue={true} /> Chronic{'  '}
        <Checkbox field={`intervention${id}Acute`} initialValue={true} /> Acute
      </td>
      <td>
        <Typeahead field={`intervention${id}`} size={16} endpoint='intervention' />
      </td>
      <td>
        <DateText field={`interventionDateLower${id}`} item={{}} size={12} initEmpty />
      </td>
      <td>
        <DateText field={`interventionDateUpper${id}`} item={{}} size={12} initEmpty />
      </td>
      <td>
        <Button onClick={() => this.deleteInterventionsRow(id)}>X</Button>
      </td>
    </tr>
  );

  toggleInterventionsHelp = () => {
    this.setState(prevState => ({
      ...prevState,
      interventionsHelp: !prevState.interventionsHelp,
    }));
  }

  addInterventionsRow = () => {
    this.setState(prevState => ({
      ...prevState,
      interventionsCounter: prevState.interventionsCounter + 1,
      interventionsIds: [...prevState.interventionsIds, prevState.interventionsCounter],
    }));
  }

  deleteInterventionsRow = (id) => {
    this.setState((prevState) => {
      const newIds = prevState.interventionsIds.filter(i => i !== id);
      return {
        ...prevState,
        interventionsIds: newIds,
      };
    });
  }

  allergiesRow = id => (
    <tr key={id}>
      <td>
        <Typeahead field={`allergen${id}`} size={16} endpoint='allergen' />
      </td>
      <td>
        <Typeahead field={`allergyReaction${id}`} size={16} endpoint='reaction' />
      </td>
      <td>
        <Checkbox field={`allergy${id}Mild`} initialValue={true} /> Mild{'  '}
        <Checkbox field={`allergy${id}Medium}`} initialValue={true} /> Medium{'  '}
        <Checkbox field={`allergy${id}Severe}`} initialValue={true} /> Severe
      </td>
      <td>
        <Button onClick={() => this.deleteAllergiesRow(id)}>X</Button>
      </td>
    </tr>
  );

  toggleAllergiesHelp = () => {
    this.setState(prevState => ({
      ...prevState,
      allergiesHelp: !prevState.allergiesHelp,
    }));
  }

  addAllergiesRow = () => {
    this.setState(prevState => ({
      ...prevState,
      allergiesCounter: prevState.allergiesCounter + 1,
      allergiesIds: [...prevState.allergiesIds, prevState.allergiesCounter],
    }));
  }

  deleteAllergiesRow = (id) => {
    this.setState((prevState) => {
      const newIds = prevState.allergiesIds.filter(i => i !== id);
      return {
        ...prevState,
        allergiesIds: newIds,
      };
    });
  }

  externalReportsRow = id => (
    <tr key={id}>
      <td>
        <Select field={`externalReportType${id}`} initialValue='any'>
          <Option value='any'>Any</Option>
          <Option value='doctorNote'>Doctor&apos;s Notes/Referrals</Option>
          <Option value='labResult'>Laboratory Results</Option>
          <Option value='xray'>X-Ray</Option>
          <Option value='ctscan'>CT Scan</Option>
          <Option value='ultrasound'>Ultrasound</Option>
          <Option value='otherImagingReport'>Other Imaging Report</Option>
          <Option value='misc'>Miscellaneous</Option>
        </Select>
      </td>
      <td>
        <Typeahead field={`externalReport${id}`} endpoint='docName' size={16} />
      </td>
      <td>
        <DateText field={`externalReportDateLower${id}`} item={{}} size={12} initEmpty />
      </td>
      <td>
        <DateText field={`externalReportDateUpper${id}`} item={{}} size={12} initEmpty />
      </td>
      <td>
        <Text field={`externalReportLocation${id}`} size={16} />
      </td>
      <td>
        <Button onClick={() => this.deleteExternalReportsRow(id)}>X</Button>
      </td>
    </tr>
  );

  toggleExternalReportsHelp = () => {
    this.setState(prevState => ({
      ...prevState,
      externalReportsHelp: !prevState.externalReportsHelp,
    }));
  }

  addExternalReportsRow = () => {
    this.setState(prevState => ({
      ...prevState,
      externalReportsCounter: prevState.externalReportsCounter + 1,
      externalReportsIds: [...prevState.externalReportsIds, prevState.externalReportsCounter],
    }));
  }

  deleteExternalReportsRow = (id) => {
    this.setState((prevState) => {
      const newIds = prevState.externalReportsIds.filter(i => i !== id);
      return {
        ...prevState,
        externalReportsIds: newIds,
      };
    });
  }

  render() {
    const houseHistoryRows = this.state.houseHistoryIds.map(this.houseHistoryRow);
    const hospitalizationHistoryRows = this.state.hospitalizationHistoryIds.map(this.hospitalizationHistoryRow);
    const dietHistoryRows = this.state.dietHistoryIds.map(this.dietHistoryRow);
    const testingRows = this.state.testingIds.map(this.testingRow);
    const problemsRows = this.state.problemsIds.map(this.problemsRow);
    const medicationsRows = this.state.medicationsIds.map(this.medicationsRow);
    const interventionsRows = this.state.interventionsIds.map(this.interventionsRow);
    const allergiesRows = this.state.allergiesIds.map(this.allergiesRow);
    const externalReportsRows = this.state.externalReportsIds.map(this.externalReportsRow);

    const advancedOptions = (
      <div>
        <ContentBox title="General Info" collapsable>
          <span onClick={this.toggleGeneralInfoHelp} className={styles.clickableText}>show help</span>
          <br />
          {this.state.generalInfoHelp && generalInfoHelp}
          {this.state.generalInfoHelp && <br />}
          <table>
            <tbody>
              <tr>
                <td><Label>Siswati Name</Label></td>
                <td>
                  <Text field='siswatiName' />
                </td>
              </tr>
              <tr>
                <td><Label>Sex:</Label></td>
                <td>
                  <Checkbox field='male' initialValue={true} /> M{'  '}
                  <Checkbox field='female' initialValue={true} /> F
                </td>
              </tr>
              <tr>
                <td><Label>Age Range Lower:</Label></td>
                <td>
                  <NumericText field='ageLowerYears' initialValue={null} placeholder={0} size={2} /> years <NumericText field='ageLowerMonths' initialValue={null} placeholder={0} size={2} /> months
                </td>
              </tr>
              <tr>
                <td><Label>Age Range Upper:</Label></td>
                <td>
                  <NumericText field='ageUpperYears' initialValue={null} placeholder={99} size={2} /> years <NumericText field='ageUpperMonths' initialValue={null} placeholder={0} size={2} /> months
                </td>
              </tr>
              <tr>
                <td><Label>Date Arrived Lower (default {MIN_DATE}):</Label></td>
                <td>
                  <DateText field='dateArrivedLower' item={{}} initEmpty />
                </td>
              </tr>
              <tr>
                <td><Label>Date Arrived Upper (default today):</Label></td>
                <td>
                  <DateText field='dateArrivedUpper' item={{}} initEmpty />
                </td>
              </tr>
              <tr>
                <td><Label>Deceased:</Label></td>
                <td>
                  <Checkbox field='deceasedYes' initialValue={false} /> Yes{'  '}
                  <Checkbox field='deceasedNo' initialValue={true} /> No
                </td>
              </tr>
              <tr>
                <td><Label>Archived:</Label></td>
                <td>
                  <Checkbox field='archivedYes' initialValue={false} /> Yes{'  '}
                  <Checkbox field='archivedNo' initialValue={true} /> No
                </td>
              </tr>
              <tr>
                <td>
                  <Label>Social History Location</Label>
                  <span data-tip data-for='socialHistoryInfo'> &#9432;</span>
                  <ReactToolTip type='info' effect='solid' id='socialHistoryInfo'>
                    Leaving this field empty has no effect. Otherwise, in order to match the query,<br />
                    the patient&apos;s social history location must contain the query (case-insensitive).<br />
                    For example, the query &quot;york&quot; would successfully match a patient&apos;s &quot;New York&quot;.
                  </ReactToolTip>
                </td>
                <td><Typeahead field='socialHistoryLocation' endpoint='socialHxLocation' /></td>
              </tr>
              <tr>
                <td><Label>Latest WCC Status</Label></td>
                <td>
                  <Checkbox field='wccNormal' initialValue={true} />Normal{'  '}
                  <Checkbox field='wccAbnormal' initialValue={true} />Abnormal{'  '}
                  <Checkbox field='wccUnknown' initialValue={true} />Unknown
                </td>
              </tr>
              <tr>
                <td>
                  <Label>Immunization Lot Number</Label>
                  <span data-tip data-for='immunizationLotInfo'> &#9432;</span>
                  <ReactToolTip type='info' effect='solid' id='immunizationLotInfo'>
                    Finds children who have received any immunization<br />
                    with the specified lot number.
                  </ReactToolTip>
                </td>
                <td><Typeahead field='immunizationLotNumber' endpoint='lotNum' /></td>
              </tr>
              <tr>
                <td><Label>Weight Percentile</Label></td>
                <td>
                  <NumericText field='weightPercentileLower' initialValue={null} placeholder={0} size={3} />
                  {' - '}
                  <NumericText field='weightPercentileUpper' initialValue={null} placeholder={100} size={3} />
                </td>
              </tr>
              <tr>
                <td><Label>Length Percentile</Label></td>
                <td>
                  <NumericText field='lengthPercentileLower' initialValue={null} placeholder={0} size={3} />
                  {' - '}
                  <NumericText field='lengthPercentileUpper' initialValue={null} placeholder={100} size={3} />
                </td>
              </tr>
            </tbody>
          </table>
        </ContentBox>
        <ContentBox title="House History" collapsable startCollapsed>
          <span onClick={this.toggleHouseHistoryHelp} className={styles.clickableText}>show help</span>
          <br />
          {this.state.houseHistoryHelp && houseHistoryHelp}
          {this.state.houseHistoryHelp && <br />}
          <Button onClick={this.addHouseHistoryRow}>Add Constraint</Button>
          <table className={styles.table}>
            <thead>
              <tr>
                <td>House</td>
                <td>Date Range Lower</td>
                <td>Date Range Upper</td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {houseHistoryRows}
            </tbody>
          </table>
        </ContentBox>
        <ContentBox title="Hospitalization History" collapsable startCollapsed>
          <span onClick={this.toggleHospitalizationHistoryHelp} className={styles.clickableText}>show help</span>
          <br />
          {this.state.hospitalizationHistoryHelp && hospitalizationHistoryHelp}
          {this.state.hospitalizationHistoryHelp && <br />}
          <Button onClick={this.addHospitalizationHistoryRow}>Add Constraint</Button>
          <table className={styles.table}>
            <thead>
              <tr>
                <td>Location</td>
                <td>Date Range Lower</td>
                <td>Date Range Upper</td>
                <td>Diagnosis</td>
                <td>Hospital Treatment</td>
                <td>Discharge Treatment</td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {hospitalizationHistoryRows}
            </tbody>
          </table>
        </ContentBox>
        <ContentBox title="Diet History" collapsable startCollapsed>
          <span onClick={this.toggleDietHistoryHelp} className={styles.clickableText}>show help</span>
          <br />
          {this.state.dietHistoryHelp && dietHistoryHelp}
          {this.state.dietHistoryHelp && <br />}
          <Button onClick={this.addDietHistoryRow}>Add Constraint</Button>
          <table className={styles.table}>
            <thead>
              <tr>
                <td>Extra Snack</td>
                <td>Jonathan Diet</td>
                <td>Date Range Lower</td>
                <td>Date Range Upper</td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {dietHistoryRows}
            </tbody>
          </table>
        </ContentBox>
        <ContentBox title="Testing" collapsable startCollapsed>
          <span onClick={this.toggleTestingHelp} className={styles.clickableText}>show help</span>
          <br />
          {this.state.testingHelp && testingHelp}
          {this.state.testingHelp && <br />}
          <Button onClick={this.addTestingRow}>Add Constraint</Button>
          <table className={styles.table}>
            <thead>
              <tr>
                <td>Test</td>
                <td>Result</td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><Label>HIV</Label></td>
                <td>
                  <Checkbox field='hivPositive' initialValue={true} /> Positive{'  '}
                  <Checkbox field='hivNegative' initialValue={true} /> Negative{'  '}
                  <Checkbox field='hivUnknown' initialValue={true} /> Unknown
                </td>
              </tr>
              <tr>
                <td><Label>TB</Label></td>
                <td>
                  <Checkbox field='tbPositive' initialValue={true} /> Positive{'  '}
                  <Checkbox field='tbNegative' initialValue={true} /> Negative{'  '}
                  <Checkbox field='tbUnknown' initialValue={true} /> Unknown
                </td>
              </tr>
              <tr>
                <td><Label>Hemoglobin Low</Label></td>
                <td>
                  <Checkbox field='hemoglobinLowYes' initialValue={true} /> Yes{'  '}
                  <Checkbox field='hemoglobinLowNo' initialValue={true} /> No
                </td>
              </tr>
              <tr>
                <td><Label>Taking Iron</Label></td>
                <td>
                  <Checkbox field='takingIronYes' initialValue={true} /> Yes{'  '}
                  <Checkbox field='takingIronNo' initialValue={true} /> No
                </td>
              </tr>
              <tr>
                <td><Label>Glucose</Label></td>
                <td>
                  <Checkbox field='glucoseLow' initialValue={true} /> Low{'  '}
                  <Checkbox field='glucoseNormal' initialValue={true} /> Normal{'  '}
                  <Checkbox field='glucoseHigh' initialValue={true} /> High{'  '}
                  <Checkbox field='glucoseUnknown' initialValue={true} /> Unknown
                </td>
              </tr>
              <tr>
                <td><Label>Syphilis</Label></td>
                <td>
                  <Checkbox field='syphilisPositive' initialValue={true} /> Positive{'  '}
                  <Checkbox field='syphilisNegative' initialValue={true} /> Negative{'  '}
                  <Checkbox field='syphilisUnknown' initialValue={true} /> Unknown
                </td>
              </tr>
              {testingRows}
            </tbody>
          </table>
        </ContentBox>
        <ContentBox title="Problems" collapsable startCollapsed>
          <span onClick={this.toggleProblemsHelp} className={styles.clickableText}>show help</span>
          <br />
          {this.state.problemsHelp && problemsHelp}
          {this.state.problemsHelp && <br />}
          <Button onClick={this.addProblemsRow}>Add Constraint</Button>
          <table className={styles.table}>
            <thead>
              <tr>
                <td>Chronic/Acute</td>
                <td>Diagnosis</td>
                <td>Date Range Lower</td>
                <td>Date Range Upper</td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {problemsRows}
            </tbody>
          </table>
        </ContentBox>
        <ContentBox title="Medications" collapsable startCollapsed>
          <span onClick={this.toggleMedicationsHelp} className={styles.clickableText}>show help</span>
          <br />
          {this.state.medicationsHelp && medicationsHelp}
          {this.state.medicationsHelp && <br />}
          <Button onClick={this.addMedicationsRow}>Add Constraint</Button>
          <table className={styles.table}>
            <thead>
              <tr>
                <td>Chronic/Acute</td>
                <td>Name of Rx</td>
                <td>By Start Date?</td>
                <td>Date Range Lower</td>
                <td>Date Range Upper</td>
                <td>Time</td>
                <td>Route</td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {medicationsRows}
            </tbody>
          </table>
        </ContentBox>
        <ContentBox title="Interventions" collapsable startCollapsed>
          <span onClick={this.toggleInterventionsHelp} className={styles.clickableText}>show help</span>
          <br />
          {this.state.interventionsHelp && interventionsHelp}
          {this.state.interventionsHelp && <br />}
          <Button onClick={this.addInterventionsRow}>Add Constraint</Button>
          <table className={styles.table}>
            <thead>
              <tr>
                <td>Chronic/Acute</td>
                <td>Intervention Name</td>
                <td>Date Range Lower</td>
                <td>Date Range Upper</td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {interventionsRows}
            </tbody>
          </table>
        </ContentBox>
        <ContentBox title="Allergies" collapsable startCollapsed>
          <span onClick={this.toggleAllergiesHelp} className={styles.clickableText}>show help</span>
          <br />
          {this.state.allergiesHelp && allergiesHelp}
          {this.state.allergiesHelp && <br />}
          <Button onClick={this.addAllergiesRow}>Add Constraint</Button>
          <table className={styles.table}>
            <thead>
              <tr>
                <td>Allergen</td>
                <td>Reaction</td>
                <td>Severity</td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {allergiesRows}
            </tbody>
          </table>
        </ContentBox>
        <ContentBox title="External Reports" collapsable startCollapsed>
          <span onClick={this.toggleExternalReportsHelp} className={styles.clickableText}>show help</span>
          <br />
          {this.state.externalReportsHelp && externalReportsHelp}
          {this.state.externalReportsHelp && <br />}
          <Button onClick={this.addExternalReportsRow}>Add Constraint</Button>
          <table className={styles.table}>
            <thead>
              <tr>
                <td>Report Type</td>
                <td>Document Name</td>
                <td>Date Range Lower</td>
                <td>Date Range Upper</td>
                <td>Author/Location</td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {externalReportsRows}
            </tbody>
          </table>
        </ContentBox>
        <Button onClick={this.onSearch}>Search</Button>
      </div>
    );

    return (
      <div>
        <div className={styles.buttonsContainer}>
          <div>
            <Text field='searchText' initialValue={''} placeholder='Name' onKeyPress={this.onKeyPress} />
            <Button onClick={this.onSearch}>Search</Button>
            <Button onClick={this.onToggleAdvancedOptions}>Advanced Options</Button>
            <span>
              <span className={styles.white} data-tip data-for='searchInfo'> &#9432;</span>
              <ReactToolTip type='info' effect='solid' id='searchInfo'>
                To search, enter space-separated keywords (case-insensitive).
              </ReactToolTip>
            </span>
          </div>
          {/* Ideally, this would not rely on the URL path. */}
          {this.props.history.location.pathname.includes('search') && (
            <div className={styles.rightAlign}>
              <Link to='/addPatient'><Button>Add Patient</Button></Link>
            </div>
          )}
        </div>
        {this.state.advanced && advancedOptions}
      </div>
    );
  }
}

// The dispatchers for fetching search results for the search page vs updates
// page variants differ only in the target redux location that the data is
// saved.

const mapDispatchToPropsSearch = dispatch => ({
  fetchSearchResults: searchParams => dispatch(fetchSearchResults(searchParams)),
  fetchSearchResultsAdvanced: searchParams => dispatch(fetchSearchResultsAdvanced(searchParams)),
});

const mapDispatchToPropsUpdates = dispatch => ({
  fetchSearchResults: searchParams => dispatch(fetchSearchResultsForUpdates(searchParams)),
  fetchSearchResultsAdvanced: searchParams => dispatch(fetchSearchResultsForUpdatesAdvanced(searchParams)),
});

export const SearchMenuForSearch = connect(
  null,
  mapDispatchToPropsSearch,
)(withRouter(SearchMenuComponent));

export const SearchMenuForUpdates = connect(
  null,
  mapDispatchToPropsUpdates,
)(withRouter(SearchMenuComponent));



// WEBPACK FOOTER //
// ./client/src/components/searchPage/SearchMenu.jsx